import { FormEvent, useEffect, useState } from 'react';
import { ModalPortalShell } from '../ui/ModalPortalShell';

type PlanningExtendDeadlineModalProps = {
  open: boolean;
  currentEndDate?: string | null;
  onClose: () => void;
  onSubmit: (payload: { newPlannedEndDate: string; reason: string }) => Promise<void>;
};

export function PlanningExtendDeadlineModal({
  open,
  currentEndDate,
  onClose,
  onSubmit,
}: PlanningExtendDeadlineModalProps) {
  const [newPlannedEndDate, setNewPlannedEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewPlannedEndDate('');
    setReason('');
  }, [open, currentEndDate]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!newPlannedEndDate || !reason.trim()) return;

    setSaving(true);
    try {
      await onSubmit({
        newPlannedEndDate,
        reason: reason.trim(),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell open={open} panelClassName="erp-card max-w-md shadow-xl">
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => void handleSubmit(event)}>
        <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
          <h2 className="erp-card-title">Estender prazo</h2>
          <p className="mt-1 text-sm text-secondary-600">
            Será criada uma nova tarefa com o novo prazo. A data fim atual permanece registrada.
          </p>
        </div>

        <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          {currentEndDate ? (
            <p className="text-sm text-secondary-700">
              Prazo atual: <span className="font-medium">{currentEndDate}</span>
            </p>
          ) : null}

          <label className="block">
            <span className="erp-label">Nova data fim</span>
            <input
              type="date"
              className="erp-input"
              value={newPlannedEndDate}
              min={currentEndDate || undefined}
              onChange={(event) => setNewPlannedEndDate(event.target.value)}
              required
            />
          </label>

          <label className="block">
            <span className="erp-label">Motivo da mudança de prazo</span>
            <textarea
              className="erp-input min-h-[96px]"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              maxLength={2000}
              placeholder="Descreva o motivo da prorrogação"
            />
          </label>
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="submit"
            className="erp-btn-primary"
            disabled={saving || !newPlannedEndDate || !reason.trim()}
          >
            {saving ? 'Salvando…' : 'Confirmar extensão'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
